export default function(ngModule) {
    /**
     * @ngdoc service
     * @name eventix.common.models.TicketReservation
     * @kind class
     * @description
     * Model that holds returned data from API reservation call and augments it with
     * product/ticket details & convenience methods
     *
     * Does *not* inherit from `base-class`
     */
    ngModule.factory('TicketReservation', function(MetaData, Product, $log, $q, LaravelValidationRules, $rootScope, Ticket, $filter, Shop, $http, $state, BCAPIMessages, OAuthTokens, syncHttp, ProductGroup, ProductReservationPool, $translate) {
        const Rules = LaravelValidationRules;
        const ISO8601 = 'YYYY-MM-DDTHH:mm:ssZ';


        function TicketReservation(reservation) {
            _.assign(this, reservation);
            this.productReservations = new ProductReservationPool(reservation.childReservations, this);
            this.reservedOn = moment();
            this.shop = Shop.cached.first();
            let reservationTime = angular.isNumber(this.shop.reservation_time) && this.shop.reservation_time > 0 ? this.shop.reservation_time : 20;
            this.expiration = moment().add(reservationTime, 'minutes').format(ISO8601);

            // A map of productGuids:[metadata]
            this.findingModels = this.findModels();
        }

        (() => {
            BCAPIMessages.apply(TicketReservation);
            TicketReservation.prototype.broadcastAPIMessage = TicketReservation.__broadcastAPIMessage;

            TicketReservation.configureAPIMessages({
                '/:guid/reserve/ticket/:tguid': {
                    400: 'models.ticket.notice.soldOut',
                    406: 'models.ticket.notice.maxReserved'
                },
                '/:guid/reserve/ticket/:tguid/product/:pguid': {
                    400: 'models.product.notice.soldOut',
                    406: 'models.product.notice.maxReserved'
                }
            });
        })();

        TicketReservation.modelName = 'TicketReservation';

        /**
         * @ngdoc method
         * @name eventix.common.models.TicketReservation#reserve
         * @methodOf eventix.common.models.TicketReservation
         * @description
         * Request a new reservation from the API and create a TicketReservation from it
         *
         * @param {String} ticketId Ticket GUID
         * @param {String} eventId Event GUID
         * @returns {Promise<TicketReservation>} Resolves with a TicketReservation instance
         */
        TicketReservation.reserve = function(ticketId, eventId) {
            return $http.get(`/${$state.params.shopId}/reserve/ticket/${ticketId}`)
                .then(response => {
                    TicketReservation.broadcastAPIMessage(response);
                    _.assign(response.data, {
                        ticket_guid: ticketId,
                        event_guid: eventId
                    });
                    const reservation = TicketReservation.new(response.data);
                    return reservation.findingModels;
                }).catch(err => {
                    TicketReservation.broadcastAPIMessage(err);
                });
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.TicketReservation#release
         * @methodOf eventix.common.models.TicketReservation
         * @description
         * Release this reservation
         * @param {Boolean} sync Release this ticket synchronously (beforeUnload)
         * The synchronous HTTP calls are used when the user closes the browser tab with active reservations.
         * We'll release those reservations, but this must be done synchronously according to the beforeUnload spec
         *
         * In this case we'll also have to release any seats reservations. Normally the seats.io integration handles
         * reserving and releasing from within their plugin. (People (de)selecting seats on a seatingplan), but since
         * we're in a beforeUnload scenario, we have to do it manually.
         * @returns {Promise<Response>} Resolves with the response object
         */
        TicketReservation.prototype.release = function(sync) {
            let expired = this.expiration < moment().add(30, 'seconds').format(ISO8601);
            let url = `/${$state.params.shopId}/reserve/ticket/${this.ticket.guid}/${this.reservation}`;
            if(!sync) {
                return this.productReservations.releaseAll()
                    .then(() => !expired ? $http.delete(url) : $q.resolve())
                    .catch((err) => {
                        this.broadcastAPIMessage(err);
                        return $q.reject(err);
                    });
            }

            this.productReservations.releaseAll(sync);

            if (this.seatInfo) {
                syncHttp('POST', 'https://app.seats.io/api/unreserve', JSON.stringify({
                    eventKeys: this.seatInfo.eventKeys,
                    objects: [this.seatInfo.uuid],
                    publicKey: this.seatInfo.publicKey,
                    reservationToken: this.seatInfo.reservationToken
                }), { 'Content-Type': 'application/json' });
            }
            return syncHttp('DELETE', SHOP_API_ROOT + url, null, {
                Authorization: _.get(OAuthTokens.client_credentials, 'access_token', '')
            });
        };

        /**
         * @ngdoc function
         * @name eventix.common.models.TicketReservation#new
         * @methodOf eventix.common.models.TicketReservation
         * @description
         * Construct a new ticket reservation from an API reservation call. Will augment the model with `metaData`, `expiration`, `reservedOn` and `$errors`
         *
         * @param {Object} reservation A ticket reservation response from the API augmented with `ticket_guid` and `event_guid`
         * ```
         * {
         *   ticket_guid: 'guid',
         *   event_guid: 'guid',
         *   reservation: 'reservation_guid',
         *   childReservations: {
         *     Product: {
         *       'product_guid': ['reservation_guid']
         *     }
         *   }
         * }
         * ```
         * @return {TicketReservation} An initialized reservation
         */
        TicketReservation.new = function(reservation) {
            return new TicketReservation(reservation);
        };

        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#event_guid
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * The event guid this ticket was registered with
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#reservation
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * The reservation guid
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#productReservations
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * A pool of product reservations
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#ticket
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * The ticket model this reservation belongs to
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#requiredProducts
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * The product models this ticket cannot be valid without. Required for correctly calculating the price, because
         * the `ticket#min_price` only overrides the value of explicitly required products.
         * (product in groups(min|max_bound==length))
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#optionalProducts
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * The products that are not within any productGroup
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#primaryGroup
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * The primary ProductGroup of this ticket (that defines its required products)
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#choices
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * Other product groups that denote choices in products
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#groups
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * All product groups
         */
        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#products
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * The product models belonging to the reservation of ticket
         */
        /**
         * @ngdoc method
         * @name eventix.common.models.TicketReservation#findModels
         * @methodOf eventix.common.models.TicketReservation
         * @description
         * Populate reservation with associated instances of `ticket`, `products` and `metaData`
         *
         * @return {Promise<TicketReservation>} Resolves with itself when it's done
         */
        TicketReservation.prototype.findModels = function() {
            const cacheOnly = true;
            this.ticket = Ticket.cached[this.ticket_guid];
            return this.ticket.$queryProduct(cacheOnly).then(products => {
                this.products = products;
                return $filter('sortByPredecessor')(this.ticket.$queryProductGroup(cacheOnly));
            }).then(groups => {
                this.groups = groups;
                return this.ticket.getRequiredProductSet();
            }).then(requiredProducts => {
                this.requiredProducts = requiredProducts;
                this.optionalProducts = _.filter(this.products, product => {
                    return !_.some(this.groups, group => _.includes(group.products, product.guid));
                });
            }).then(() => this);
        };

        /**
         * @ngdoc property
         * @name eventix.common.models.TicketReservation#price
         * @propertyOf eventix.common.models.TicketReservation
         * @description
         * The calculated price, available after `calculatePrice` has been called
         */

        /**
         * @ngdoc method
         * @name eventix.common.models.TicketReservation#calculatePrice
         * @methodOf eventix.common.models.TicketReservation
         * @description
         * Calculate price of reserved products in this ticketReservation
         *
         * @param {ShopCoupon} [coupon] Optional coupon instance, which may or may not affect price, depending
         * whether the coupon is applied to products in this reservation. (This application/assignment is done
         * in `OrderManager#updateOrderTotal`)
         * @returns {Number} Total price
         */
        TicketReservation.prototype.calculatePrice = function(coupon) {
            let total = this.ticket.min_price || 0;
            for(let reservation of this.productReservations.each()) {
                let isRequired = _.some(this.requiredProducts, { guid: reservation.productId });
                let product = _.find(this.products, { guid: reservation.productId });
                if(coupon) {
                    if(isRequired && this.ticket.min_price)
                        total -= coupon.calculateProductDiscount(product, reservation.guid);
                    else
                        total += coupon.calculateProductPrice(product, reservation.guid);
                } else {
                    if(isRequired && this.ticket.min_price)
                        continue;
                    total += product.ticketPrice;
                }
            }
            // Only calculate service costs for a ticketprice > 0
            if(total > 0){
                let varying = total * this.ticket.service_cost_percentage;
                total += _.toInteger(this.ticket.service_cost) + _.toInteger(varying);
            }

            this.price = total;
            return total;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.TicketReservation#$invalid
         * @methodOf eventix.common.models.TicketReservation
         * @returns {Boolean|String} False if everything is valid, can return a user-friendly error message if there's an invalid group.
         */
        TicketReservation.prototype.$invalid = function() {
            for(let group of this.groups) {
                if(!this.productReservations.satisfies(group))
                    return $translate.instant('models.productGroup.notice.invalid', group);
            }

            if (this.productReservations.invalid()) {
                for(let productReservation of this.productReservations) {
                    this.$errors = _.defaults(this.$errors, productReservation.$errors);
                }

                return true;
            }

            return false;
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.TicketReservation#prepareData
         * @methodOf eventix.common.models.TicketReservation
         * @returns {Object} Data object for submission to API#placeShopOrder
         */
        TicketReservation.prototype.prepareData = function() {
            const r = this;
            var data = {
                guid: r.ticket_guid,
                reservation: r.reservation,
                products: [],
                metaData: []
            };

            for(let reservation of this.productReservations.each()) {
                const productData = {
                    guid: reservation.productId,
                    reservation: reservation.guid,
                    metaData: []
                };
                _.forEach(reservation.metaData, (value, guid) => {
                    let metaData = MetaData.cached[guid];
                    if(metaData.applies_to === 'person') {
                        // Prevent duplicates of person-level metadata
                        if(_.some(data.metaData, { guid }))
                            return;
                        data.metaData.push({ guid, value });
                    } else
                        productData.metaData.push({ guid, value });
                });
                data.products.push(productData);
            }

            if(this.seatInfo) {
                data.seatInfo = {
                    eventKeys: this.seatInfo.eventKeys,
                    uuid: this.seatInfo.uuid
                };
            }
            return data;
        };

        return TicketReservation;
    });
}
